'use client';

import { motion } from 'framer-motion';
import { ArrowRight, Search, LayoutDashboard, FileSearch, Globe2, Keyboard, Lightbulb } from 'lucide-react';
import Link from 'next/link';
import { SHORTCUT_GROUPS } from '@/components/terminal/ShortcutsHelp';

function Key({ k }: { k: string }) {
  return (
    <kbd
      className="font-mono text-[11px] font-semibold px-1.5 py-0.5 rounded border min-w-[20px] text-center inline-block"
      style={{
        backgroundColor: 'var(--color-surface)',
        borderColor: 'var(--color-border-subtle)',
        color: 'var(--color-text)',
      }}
    >
      {k}
    </kbd>
  );
}

function KeyCombo({ keys }: { keys: string[] }) {
  return (
    <span className="inline-flex items-center gap-1">
      {keys.map((k, i) => (
        <span key={i} className="inline-flex items-center gap-1">
          {i > 0 && <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>then</span>}
          <Key k={k} />
        </span>
      ))}
    </span>
  );
}

const SECTIONS = [
  {
    id: 'overview',
    label: 'Overview',
    icon: <Lightbulb size={16} />,
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: <LayoutDashboard size={16} />,
  },
  {
    id: 'research',
    label: 'Research',
    icon: <FileSearch size={16} />,
  },
  {
    id: 'market-data',
    label: 'Market Data',
    icon: <Globe2 size={16} />,
  },
  {
    id: 'shortcuts',
    label: 'Shortcuts',
    icon: <Keyboard size={16} />,
  },
];

export default function TerminalGuidePage() {
  return (
    <>
      {/* Anchor nav */}
      <nav
        aria-label="Section navigation"
        className="sticky top-[3.5rem] z-30 backdrop-blur-md"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--color-bg) 85%, transparent)',
          borderBottom: '1px solid var(--color-border-subtle)',
        }}
      >
        <div className="container mx-auto px-4">
          <ul className="flex items-center gap-1 overflow-x-auto py-3">
            {SECTIONS.map((s) => (
              <li key={s.id} className="flex-shrink-0">
                <a
                  href={`#${s.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors hover:bg-[var(--color-surface)]"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {s.icon}
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-12 sm:py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <span
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono font-semibold uppercase tracking-wider mb-5 border"
              style={{
                color: 'var(--color-accent-muted)',
                backgroundColor: 'color-mix(in srgb, var(--color-accent) 8%, transparent)',
                borderColor: 'color-mix(in srgb, var(--color-accent) 25%, transparent)',
              }}
            >
              <Keyboard size={14} />
              Terminal guide
            </span>
            <h1 className="text-3xl sm:text-5xl font-bold tracking-tight text-[var(--color-text)]">
              How to use Terminal mode.
            </h1>
            <p className="mt-5 text-lg leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
              Terminal is a dense, keyboard-first view of the markets — three workflows on one screen,
              navigated by hotkey. This guide walks through each view and lists every shortcut.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                href="/terminal"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-colors bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]"
              >
                Open Terminal
                <ArrowRight size={16} />
              </Link>
              <a
                href="#shortcuts"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium border transition-colors hover:bg-[var(--color-surface-secondary)]"
                style={{ color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' }}
              >
                Jump to shortcuts
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section id="overview" className="py-10 scroll-mt-24">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-bold text-[var(--color-text)] mb-4">The three views</h2>
          <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--color-text-secondary)' }}>
            Every feature in Terminal lives in one of three views. You can move between them with the
            buttons in the top bar — or with hotkeys.
          </p>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              {
                icon: <LayoutDashboard size={20} />,
                title: 'Dashboard',
                body: 'Your watchlist, indices, market movers, news, and a price chart for the selected symbol.',
                hotkey: ['g', 'd'],
              },
              {
                icon: <FileSearch size={20} />,
                title: 'Research',
                body: 'Deep-dive on a single ticker — Overview, Financials, and Risk tabs.',
                hotkey: ['g', 'r'],
              },
              {
                icon: <Globe2 size={20} />,
                title: 'Market Data',
                body: 'Indices with their constituents, commodities, the SPX-vs-VIX chart, and macro indicators.',
                hotkey: ['g', 'm'],
              },
            ].map((card) => (
              <div
                key={card.title}
                className="rounded-2xl p-5 border"
                style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border-subtle)' }}
              >
                <div
                  className="inline-flex items-center justify-center w-10 h-10 rounded-xl mb-3"
                  style={{
                    backgroundColor: 'color-mix(in srgb, var(--color-accent) 15%, transparent)',
                    color: 'var(--color-accent-muted)',
                  }}
                >
                  {card.icon}
                </div>
                <h3 className="text-lg font-semibold text-[var(--color-text)] mb-2">{card.title}</h3>
                <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--color-text-secondary)' }}>
                  {card.body}
                </p>
                <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                  <span>Hotkey:</span>
                  <KeyCombo keys={card.hotkey} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dashboard */}
      <section id="dashboard" className="py-10 scroll-mt-24">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-bold text-[var(--color-text)] mb-4">Dashboard</h2>
          <p className="text-base leading-relaxed mb-5" style={{ color: 'var(--color-text-secondary)' }}>
            The default view. Everything is visible without scrolling: indices at the top, your watchlist
            on the left, a price chart in the middle, market movers and news at the bottom.
          </p>
          <div className="space-y-4">
            <Step
              n={1}
              title="Add a ticker"
              body={
                <>
                  Press <Key k="/" /> or <Key k="⌘" /> <Key k="K" /> to focus the search box. Type a few
                  letters, use <Key k="↑" /> / <Key k="↓" /> to highlight a result, then <Key k="Enter" />
                  to add it to your watchlist. Hit <Key k="Enter" /> with no result selected to jump straight
                  into Research for the typed symbol.
                </>
              }
            />
            <Step
              n={2}
              title="Cycle through your watchlist"
              body={
                <>
                  Press <Key k="]" /> for the next symbol or <Key k="[" /> for the previous one. The price
                  chart, news feed, and the &quot;Research&quot; button in the top bar all retarget instantly.
                </>
              }
            />
            <Step
              n={3}
              title="Open Research for the active symbol"
              body={
                <>
                  Press <KeyCombo keys={['g', 'r']} /> to research the symbol you&apos;re currently looking at,
                  or click the <em>Research [TICKER]</em> button in the top bar.
                </>
              }
            />
          </div>
        </div>
      </section>

      {/* Research */}
      <section id="research" className="py-10 scroll-mt-24">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-bold text-[var(--color-text)] mb-4">Research view</h2>
          <p className="text-base leading-relaxed mb-5" style={{ color: 'var(--color-text-secondary)' }}>
            Three tabs cover everything you need on a ticker. Switch tabs by clicking, or with the number
            keys.
          </p>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              {
                key: '1',
                title: 'Overview',
                body: 'Price chart with multiple time ranges, the stat grid (P/E, EPS, 52W high/low, market cap, beta, volume…), and recent news for the ticker.',
              },
              {
                key: '2',
                title: 'Financials',
                body: 'Quarterly revenue chart, profitability margins, balance sheet metrics, and cash flow.',
                pro: true,
              },
              {
                key: '3',
                title: 'Risk',
                body: 'Beta gauge, short interest as a percentage of float, 52-week range with a price marker, and debt metrics.',
                pro: true,
              },
            ].map((tab) => (
              <div
                key={tab.title}
                className="rounded-2xl p-5 border"
                style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border-subtle)' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Key k={tab.key} />
                  <h3 className="text-lg font-semibold text-[var(--color-text)]">{tab.title}</h3>
                  {tab.pro && (
                    <span
                      className="ml-auto px-1.5 py-0.5 rounded text-[9px] font-mono font-semibold uppercase tracking-wider"
                      style={{
                        backgroundColor: 'color-mix(in srgb, var(--color-accent) 18%, transparent)',
                        color: 'var(--color-accent-muted)',
                      }}
                    >
                      Pro
                    </span>
                  )}
                </div>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                  {tab.body}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-5 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Press <Key k="Esc" /> to return to the dashboard. While in Research, <Key k="[" /> and{' '}
            <Key k="]" /> still cycle through your watchlist — the research view follows.
          </p>
        </div>
      </section>

      {/* Market Data */}
      <section id="market-data" className="py-10 scroll-mt-24">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-bold text-[var(--color-text)] mb-4">Market Data view</h2>
          <p className="text-base leading-relaxed mb-5" style={{ color: 'var(--color-text-secondary)' }}>
            Open with <KeyCombo keys={['g', 'm']} /> or the <em>Market Data</em> button. Use it to see
            what&apos;s inside the major indices, scan commodities, and check macro indicators.
          </p>
          <ul className="space-y-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            <li>
              <strong className="text-[var(--color-text)]">Index drilldown:</strong> tabs for NASDAQ 100,
              Russell 2000, and grouped dropdowns for the S&amp;P (500/100/MidCap 400/SmallCap 600/Oil &amp; Gas/Real
              Estate) and Dow Jones (Industrial/Composite/Transportation/Utilities) families. Each index has a
              price chart and a constituent table — click any row to open Research.
            </li>
            <li>
              <strong className="text-[var(--color-text)]">Commodities:</strong> a dedicated tab for crude oil
              (WTI), gold, and silver.
            </li>
            <li>
              <strong className="text-[var(--color-text)]">SPX vs VIX:</strong> a dual-line chart that plots
              the S&amp;P 500 against the volatility index, with an explanation of the inverse relationship.
            </li>
            <li>
              <strong className="text-[var(--color-text)]">Macro:</strong> Treasury yield curve plus the Fed
              funds rate, CPI, unemployment, and GDP.
            </li>
          </ul>
          <p className="mt-5 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Number keys jump straight to a tab: <Key k="1" /> S&amp;P 500, <Key k="2" /> Dow Jones,{' '}
            <Key k="3" /> NASDAQ 100, <Key k="4" /> Russell 2000, <Key k="5" /> Commodities,{' '}
            <Key k="6" /> VIX, <Key k="7" /> Macro.
          </p>
        </div>
      </section>

      {/* Shortcuts */}
      <section id="shortcuts" className="py-12 scroll-mt-24" style={{ backgroundColor: 'var(--color-surface-secondary)' }}>
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <div
              className="inline-flex items-center justify-center w-10 h-10 rounded-xl"
              style={{
                backgroundColor: 'color-mix(in srgb, var(--color-accent) 15%, transparent)',
                color: 'var(--color-accent-muted)',
              }}
            >
              <Keyboard size={20} />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[var(--color-text)]">Keyboard shortcuts</h2>
          </div>
          <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--color-text-secondary)' }}>
            Press <Key k="?" /> any time inside Terminal to bring up a quick reference. Shortcuts are
            disabled while you&apos;re typing in an input.
          </p>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
            {SHORTCUT_GROUPS.map((g) => (
              <div key={g.label}>
                <div
                  className="text-[10px] font-mono font-semibold uppercase tracking-wider mb-3"
                  style={{ color: 'var(--color-accent-muted)' }}
                >
                  {g.label}
                </div>
                <ul className="space-y-2">
                  {g.items.map((s, i) => (
                    <li key={i} className="flex items-center justify-between gap-3 text-sm">
                      <span style={{ color: 'var(--color-text-secondary)' }}>{s.description}</span>
                      <KeyCombo keys={s.keys} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tips */}
      <section className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl font-bold text-[var(--color-text)] mb-5 flex items-center gap-2">
            <Lightbulb size={22} className="text-[var(--color-accent-muted)]" />
            Tips
          </h2>
          <ul className="space-y-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            <li className="flex items-start gap-2">
              <Search size={16} className="mt-0.5 flex-shrink-0 text-[var(--color-accent-muted)]" />
              <span>
                Hitting <Key k="Enter" /> in the search box with no dropdown selection opens Research for the
                typed symbol — useful when you want to look at something without committing to the watchlist.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Keyboard size={16} className="mt-0.5 flex-shrink-0 text-[var(--color-accent-muted)]" />
              <span>
                The <Key k="g" /> prefix is held for ~1 second. If you press <Key k="g" /> and don&apos;t
                follow with a destination key, nothing happens.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Keyboard size={16} className="mt-0.5 flex-shrink-0 text-[var(--color-accent-muted)]" />
              <span>
                <Key k="Esc" /> works from anywhere — even while a dropdown is open in search — to go back to
                the dashboard.
              </span>
            </li>
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div
            className="rounded-3xl p-8 sm:p-10 text-center border max-w-3xl mx-auto"
            style={{
              borderColor: 'var(--color-border-subtle)',
              background:
                'linear-gradient(135deg, color-mix(in srgb, var(--color-accent) 14%, var(--color-surface)) 0%, var(--color-surface) 100%)',
            }}
          >
            <h2 className="text-2xl font-bold text-[var(--color-text)]">Ready to try it?</h2>
            <p className="mt-3 text-base max-w-xl mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
              Open Terminal and press <Key k="?" /> to see this reference at any time.
            </p>
            <div className="mt-5">
              <Link
                href="/terminal"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-colors bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]"
              >
                Open Terminal
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Step({ n, title, body }: { n: number; title: string; body: React.ReactNode }) {
  return (
    <div
      className="rounded-2xl p-5 border flex items-start gap-4"
      style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border-subtle)' }}
    >
      <div
        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-mono text-sm font-bold"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--color-accent) 18%, transparent)',
          color: 'var(--color-accent-muted)',
        }}
      >
        {n}
      </div>
      <div>
        <h3 className="text-lg font-semibold text-[var(--color-text)] mb-1">{title}</h3>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
          {body}
        </p>
      </div>
    </div>
  );
}
